import {
  useCanAccess,
  useCreate,
  useGetIdentity,
  useGetList,
  useUpdate,
  type Identifier,
} from "ra-core";

import { osloDateKey } from "../../misc/osloDate";
import { useConfigurationContext } from "../../root/ConfigurationContext";
import type { Scope } from "./ranking";

// Valgene på Oversikt huskes per bruker (fiks 7): Mine/Team/Uten eier,
// Oppgaver/Kunder, ny eller gammel oversikt og hurtigtast-hintet. Uten
// lagret valg gjelder rollens standard fra Innstillinger → Generelt →
// Oversikt (administrator: Team, medarbeider: Mine).

export type DashboardMode = "items" | "customers";
export type DashboardLayout = "fokus" | "gammel";

export type DashboardPrefs = {
  id: Identifier;
  sales_id: Identifier;
  scope?: Scope | null;
  mode?: DashboardMode | null;
  layout?: DashboardLayout | null;
  hide_shortcut_hint?: boolean;
  updated_at?: string;
};

export type DashboardDefaults = { admin?: Scope; member?: Scope };

/**
 * «Gammel oversikt» i brukermenyen finnes i to uker etter lanseringen
 * (spesifikasjonen kap. 16). Etter datoen gjelder den nye for alle.
 */
export const OLD_LAYOUT_UNTIL = "2026-11-08";

export const oldLayoutAvailable = (today = osloDateKey()) =>
  today <= OLD_LAYOUT_UNTIL;

export const isOldLayout = (layout: DashboardLayout) =>
  layout === "gammel" && oldLayoutAvailable();

export const DEFAULT_SCOPES: Required<DashboardDefaults> = {
  admin: "team",
  member: "mine",
};

export const useDashboardPrefs = () => {
  const { identity, isPending: identityPending } = useGetIdentity();
  const { canAccess: isAdmin = false } = useCanAccess({
    resource: "sales",
    action: "list",
  });
  const { dashboardDefaults } = useConfigurationContext();
  const me = identity?.id;
  const { data = [], isPending } = useGetList<DashboardPrefs>(
    "dashboard_prefs",
    {
      filter: { sales_id: me },
      pagination: { page: 1, perPage: 1 },
      sort: { field: "id", order: "ASC" },
    },
    { enabled: me != null },
  );
  const prefs = data.find((p) => String(p.sales_id) === String(me));
  const [create] = useCreate<DashboardPrefs>();
  const [update] = useUpdate<DashboardPrefs>();

  const roleDefault: Scope =
    (isAdmin ? dashboardDefaults?.admin : dashboardDefaults?.member) ??
    (isAdmin ? DEFAULT_SCOPES.admin : DEFAULT_SCOPES.member);
  const scope: Scope = prefs?.scope ?? roleDefault;
  const mode: DashboardMode = prefs?.mode ?? "items";

  const save = (change: Partial<Omit<DashboardPrefs, "id" | "sales_id">>) => {
    if (me == null) return;
    const data = { ...change, updated_at: new Date().toISOString() };
    if (prefs) {
      update(
        "dashboard_prefs",
        { id: prefs.id, data, previousData: prefs },
        { mutationMode: "optimistic" },
      );
    } else {
      create("dashboard_prefs", { data: { ...data, sales_id: me } });
    }
  };

  return {
    isPending: identityPending || (me != null && isPending),
    scope,
    mode,
    layout: (prefs?.layout ?? "fokus") as DashboardLayout,
    hideShortcutHint: !!prefs?.hide_shortcut_hint,
    /** Valgt visning er ulik rollens standard («Husket fra sist»). */
    remembered:
      (prefs?.scope != null && prefs.scope !== roleDefault) ||
      (prefs?.mode != null && prefs.mode !== "items"),
    save,
  };
};
